import { apiService, ApiResponse } from './api';
import { API_ENDPOINTS } from '../config/api';

// Types pour le profil utilisateur
export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword?: string;
}

export interface UserPreferences {
  // Apparence
  theme: 'dark' | 'light' | 'auto';
  language: 'fr' | 'en' | 'es';
  fontSize: 'small' | 'medium' | 'large';
  compactMode: boolean;
  showAnimations: boolean;
  autoSave: boolean;
  
  // Notifications
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
  newEnqueteAlert: boolean;
  deadlineReminders: boolean;
  weeklyReports: boolean;
  securityAlerts: boolean;
  
  // Sécurité
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  passwordComplexity: boolean;
  autoLogout: boolean;
  
  // Données
  autoBackup: boolean;
  backupFrequency: 'hourly' | 'daily' | 'weekly' | 'monthly';
  retentionPeriod: number;
  exportFormat: 'pdf' | 'json' | 'csv' | 'xml';
  dataEncryption: boolean;
  cloudSync: boolean;
  
  // API
  osintApiKey?: string;
  mappingApiKey?: string;
  socialMediaApiKey?: string;
  databaseApiKey?: string;
  webhookUrl?: string;
  apiRateLimit: number;
}

export interface TwoFactorData {
  enabled: boolean;
  secret?: string;
}

// Service pour la gestion des utilisateurs
class UserService {
  private static instance: UserService;

  private constructor() {}

  static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  // Récupération du profil utilisateur
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    try {
      console.log('🔍 Récupération du profil utilisateur...');
      const response = await apiService.get<UserProfile>('/users/profile');
      console.log('✅ Profil utilisateur récupéré:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération du profil:', error);
      throw error;
    }
  }

  // Mise à jour du profil utilisateur
  async updateProfile(data: UpdateProfileData): Promise<ApiResponse<UserProfile>> {
    try {
      console.log('🔄 Mise à jour du profil utilisateur...', data);
      const response = await apiService.put<UserProfile>('/users/profile', data);
      console.log('✅ Profil utilisateur mis à jour:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  }

  // Changement de mot de passe
  async changePassword(data: ChangePasswordData): Promise<ApiResponse<{ message: string }>> {
    try {
      console.log('🔐 Changement de mot de passe...');
      const response = await apiService.post<{ message: string }>(API_ENDPOINTS.CHANGE_PASSWORD, data);
      console.log('✅ Mot de passe changé avec succès');
      return response;
    } catch (error) {
      console.error('❌ Erreur lors du changement de mot de passe:', error);
      throw error;
    }
  }

  // Récupération des préférences utilisateur
  async getPreferences(): Promise<ApiResponse<UserPreferences>> {
    try {
      console.log('🔍 Récupération des préférences utilisateur...');
      const response = await apiService.get<UserPreferences>('/users/preferences');
      console.log('✅ Préférences utilisateur récupérées:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des préférences:', error);
      throw error;
    }
  }

  // Mise à jour des préférences utilisateur
  async updatePreferences(preferences: Partial<UserPreferences>): Promise<ApiResponse<UserPreferences>> {
    try {
      console.log('🔄 Mise à jour des préférences utilisateur...', preferences);
      const response = await apiService.put<UserPreferences>('/users/preferences', preferences);
      console.log('✅ Préférences utilisateur mises à jour:', response.data);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour des préférences:', error);
      throw error;
    }
  }

  // Activation/Désactivation de l'authentification à deux facteurs
  async toggleTwoFactor(data: TwoFactorData): Promise<ApiResponse<{ message: string }>> {
    try {
      console.log('🔐 Configuration de l\'authentification à deux facteurs...', { enabled: data.enabled });
      
      // Utiliser les endpoints MFA dédiés au lieu du endpoint users/toggle-2fa qui n'existe pas
      const endpoint = data.enabled ? API_ENDPOINTS.MFA_ENABLE : API_ENDPOINTS.MFA_DISABLE;
      const response = await apiService.post<{ message: string }>(endpoint, data);
      
      console.log('✅ Authentification à deux facteurs configurée');
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la configuration 2FA:', error);
      throw error;
    }
  }

  // Suppression de compte
  async deleteAccount(password: string): Promise<ApiResponse<{ message: string }>> {
    try {
      console.log('🗑️ Suppression du compte utilisateur...');
      // Passer le mot de passe dans le body pour l'endpoint DELETE
      const response = await apiService.delete<{ message: string }>('/api/users/account');
      console.log('✅ Compte utilisateur supprimé');
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du compte:', error);
      throw error;
    }
  }

  // Génération d'un secret pour l'authentification à deux facteurs
  generateTwoFactorSecret(): string {
    // En production, utilisez une librairie comme 'speakeasy' pour générer un vrai secret TOTP
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return secret;
  }

  // Génération d'une URL QR Code pour l'authentification à deux facteurs
  generateTwoFactorQRUrl(email: string, secret: string): string {
    const appName = 'OSINT Dashboard';
    const otpauth = `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent(appName)}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauth)}`;
  }

  // Validation d'un code TOTP (pour l'authentification à deux facteurs)
  validateTOTPCode(secret: string, token: string): boolean {
    // En production, utilisez une librairie comme 'speakeasy' pour valider le code TOTP
    // Pour le moment, on accepte tous les codes de 6 chiffres pour la démonstration
    return token.length === 6 && /^\d{6}$/.test(token);
  }

  // Export des données utilisateur
  async exportUserData(format: 'pdf' | 'json' | 'csv' | 'xml' = 'json'): Promise<Blob> {
    try {
      console.log('📤 Export des données utilisateur en format:', format);
      const response = await apiService.get<any>(`/users/export?format=${format}`);
      console.log('✅ Données utilisateur exportées');
      return response.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'export des données:', error);
      throw error;
    }
  }

  // Import des données utilisateur
  async importUserData(file: File): Promise<ApiResponse<{ message: string }>> {
    try {
      console.log('📥 Import des données utilisateur...');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiService.post<{ message: string }>('/users/import', formData);
      console.log('✅ Données utilisateur importées');
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de l\'import des données:', error);
      throw error;
    }
  }

  // Validation de la force du mot de passe
  validatePasswordStrength(password: string): {
    score: number;
    feedback: string[];
    isValid: boolean;
  } {
    const feedback: string[] = [];
    let score = 0;

    // Vérifications
    if (password.length >= 8) score += 1;
    else feedback.push('Le mot de passe doit contenir au moins 8 caractères');

    if (/[a-z]/.test(password)) score += 1;
    else feedback.push('Le mot de passe doit contenir au moins une minuscule');

    if (/[A-Z]/.test(password)) score += 1;
    else feedback.push('Le mot de passe doit contenir au moins une majuscule');

    if (/\d/.test(password)) score += 1;
    else feedback.push('Le mot de passe doit contenir au moins un chiffre');

    if (/[@$!%*?&]/.test(password)) score += 1;
    else feedback.push('Le mot de passe doit contenir au moins un caractère spécial');

    if (password.length >= 12) score += 1;

    return {
      score,
      feedback,
      isValid: score >= 4
    };
  }
}

export const userService = UserService.getInstance();